import { useCallStore } from '@/store/callStore'
import { usePresenceStore } from '@/store/presenceStore'
import type { VideoCall } from '@/types'
import { releaseEndedOrder } from './anchorOrderRelease'
import { shengwangClient } from './shengwangClient'
import { message } from 'antd'

const RTC_REMOTE_LEFT_RELEASE_DELAY_MS = 1000
const pendingRTCRemoteLeftTimers = new Map<string, number>()

export function registerRTCRemoteLeaveFallback() {
  return shengwangClient.subscribeRemoteUserLeft(scheduleRTCRemoteLeftRelease)
}

function scheduleRTCRemoteLeftRelease(call: VideoCall) {
  const orderID = call.order_id || ''
  if (!orderID || pendingRTCRemoteLeftTimers.has(orderID)) {
    return
  }

  const timer = window.setTimeout(() => {
    pendingRTCRemoteLeftTimers.delete(orderID)
    void releaseIfStillCurrentRemoteLeftOrder(orderID)
  }, RTC_REMOTE_LEFT_RELEASE_DELAY_MS)
  pendingRTCRemoteLeftTimers.set(orderID, timer)
}

async function releaseIfStillCurrentRemoteLeftOrder(orderID: string) {
  const calls = useCallStore.getState()
  const currentOrderID = calls.currentCall?.order_id || ''
  if (!calls.currentCall || currentOrderID !== orderID || calls.endingCallOrderID === orderID) {
    return
  }

  calls.setEndingCallOrderID(orderID)
  const endedCall = {
    ...calls.currentCall,
    status: 'ended' as const,
  }
  try {
    await shengwangClient.leaveCall().catch(() => undefined)
    const latest = useCallStore.getState()
    if (latest.currentCall?.order_id !== orderID) {
      return
    }
    latest.setCurrentCall(null)
    latest.setLastEndedCall(endedCall)
    usePresenceStore.getState().setCallStatus('idle')
    message.info('User left the call')
    await releaseEndedOrder(orderID, 'user_hangup').catch(() => undefined)
  } finally {
    const latest = useCallStore.getState()
    if (latest.endingCallOrderID === orderID) {
      latest.setEndingCallOrderID(null)
    }
  }
}

export const __rtcRemoteLeaveFallbackTestHooks = {
  reset() {
    pendingRTCRemoteLeftTimers.forEach((timer) => window.clearTimeout(timer))
    pendingRTCRemoteLeftTimers.clear()
  },
  scheduleRTCRemoteLeftRelease,
}
